<template>
  <page-view
    class="subpage"
    title="Customers"
    backLink="/dashboard"
    backText="Back to Dashboard"
    :key="$route.hash"
    :overflow="false"
  >
    <template v-slot:tabs>
      <page-tab title="All" :active="currentTab === ''" @click.native="handleLink($event, `/customers`)" :link="`/customers`" icon="list-bullets-24" />
      <page-tab title="Giveaways" :active="currentTab === 'giveaway'" @click.native="handleLink($event, `/customers/giveaway`)" :link="`/customers/giveaway`" icon="gift-24" />
      <page-tab title="Forms" :active="currentTab === 'form'" @click.native="handleLink($event, `/customers/form`)" :link="`/customers/form`" icon="edit-pen2" />
    </template>

    <template v-if="crmAvailable">
      <div class="d-f f-g fd-r f-wrap f-wrap-3 m-b-4" v-if="currentTab === ''">
        <KeyAdvanced label="Last broadcast" icon="user-24" :total="stats.last.in" :hideLive="true" :hideRecorded="true" renameTotal="New" />
        <KeyAdvanced label="Last month" icon="user-24" :total="stats.month.in" :live="stats.month.out" :hideRecorded="true" renameTotal="New" renameLive="Opted out" />
        <KeyAdvanced label="All time" icon="user-24" :total="stats.total.in" :live="stats.total.out" :hideRecorded="true" renameTotal="New" renameLive="Opted out" />
      </div>
      <router-view />
    </template>
    <template v-else>
      <div class="d-f f-1 ai-c jc-c">
        <div class="ta-c page-placeholder-text p-a-4">
          <template v-if="stats.total.in >= 50">
            <h2>Unlock access to {{ stats.total.in }} customers</h2>
          </template>
          <template v-else-if="stats.total.in > 0 && stats.total.in < 50">
            <h2>Unlock access to your customers</h2>
          </template>
          <template v-else>
            <h2>Feature not included in your current plan</h2>
          </template>
          <template v-if="activeSubscription && !hasSubscriptionPlan">
            <template v-if="stats.total.in > 0">
              <p>Get in touch with sales to gain access to collected customer data</p>
              <Button @click.prevent="contactSales.show = true;" class="m-t-4">Contact sales</Button>
            </template>
            <template v-else>
              <p>Get in touch with sales to gain access to the Customers feature</p>
              <Button @click.prevent="contactSales.show = true;" class="m-t-4">Contact sales</Button>
            </template>
          </template>
          <template v-else>
            <template v-if="stats.total.in > 0">
              <p>Upgrade your plan to gain access to collected customer data</p>
              <Button @click.prevent="$router.push('/settings/plan')" class="m-t-4">Upgrade plan</Button>
            </template>
            <template v-else>
              <p>Upgrade your plan to gain access to the Customers feature</p>
              <Button @click.prevent="$router.push('/settings/plan')" class="m-t-4">Upgrade plan</Button>
            </template>
          </template>
        </div>
      </div>
    </template>

    <modal :show="contactSales.show" @close="contactSales.show = false;" title="Contact sales" class="auto-height">
      <div class="p-a-4">
        <p class="ta-c" v-if="contactSales.state === 'sent'">We will get in touch soon!</p>
        <p class="ta-c" v-else>Do you wish to get in contact with sales regarding your plan?</p>
        <div class="d-f m-t-4">
          <Button @click="sendContact" :loading="contactSales.state === 'sending'" :disabled="contactSales.state === 'sending'" :class="`${contactSales.state === 'sent' ? 'green' : ''}`" v-if="contactSales.state !== 'sent'">Yes</Button>
          <Button @click="contactSales.show = false;" :disabled="['sending', 'send'].includes(contactSales.state)" class="red outline ml-a">
            <template v-if="contactSales.state === 'sent'">
              Close
            </template>
            <template v-else>
              Cancel
            </template>
          </Button>
        </div>
      </div>
    </modal>
  </page-view>
</template>

<script>

import moment from 'moment';

import { AnalyticsService } from '@/services/analytics.service';

import KeyAdvanced from '@/components/Statistics/KeyAdvanced.vue';

export default {
  components: {
    KeyAdvanced,
  },
  computed: {
    /**
     * Return the current tab
     * @return {String}
     */
    currentTab() {
      const tab = this.$route.path.split('/').pop();
      if (tab === 'customers') {
        return '';
      }

      return tab;
    },
    /**
     * Return boolean if CRM is available
     * @returns {Boolean}
     */
    crmAvailable() {
      return this.accountSettings?.subscription?.crm;
    },
  },
  methods: {
    /**
     * Get statistics for account and broadcasts
     * @returns {Void}
     */
    async getStatistics() {
      const {
        broadcasts,
      } = await this.$store.dispatch('Broadcasts/getBroadcasts', {
        page: 1,
        per_page: 1,
        type: 'past',
        details: 'shopping',
        direction: 'desc',
        skipConf: true,
        skipLoading: true,
        skipState: true,
      });

      let broadcastStats = [];

      if (broadcasts[0]) {
        broadcastStats = await AnalyticsService.getBroadcastTotalStatistics(broadcasts[0].id);
      }
      const monthData = await AnalyticsService.getAccountStatistics(moment().subtract(30, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
      const totalData = await AnalyticsService.getAccountTotalStatistics();

      this.stats.last.in = broadcastStats.find(e => e.event === 'crm_opted_in')?.sum;
      this.stats.last.out = broadcastStats.find(e => e.event === 'crm_opted_out')?.sum;
      this.stats.month.in = monthData.find(e => e.event === 'crm_opted_in')?.sum;
      this.stats.month.out = monthData.find(e => e.event === 'crm_opted_out')?.sum;
      this.stats.total.in = totalData.find(e => e.event === 'crm_opted_in')?.sum;
      this.stats.total.out = totalData.find(e => e.event === 'crm_opted_out')?.sum;
    },
    /**
     * Send hubspot form regarding contacting of sales with customers company name and user email
     * @returns {Void}
     */
    async sendContact() {
      this.contactSales.state = 'sending';

      const postData = {
        fields: [
          { name: 'company', value: this.account.company.name || this.fqdn },
          { name: 'email', value: this.user.email },
          { name: 'reason_for_upgrade', value: 'CRM - Customers' },

        ],
        context: {
          pageUri: window.location.href,
          pageName: 'Customers',
        },
      };

      const response = await fetch('https://api.hsforms.com/submissions/v3/integration/submit/19878004/60ce3020-63fb-44e7-a60b-de135a746161', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData),
      }).then(() => true).catch(() => false);

      if (!response) {
        swal({ title: 'An error occured', text: 'For some reason we could not send this request right now', icon: 'error' });
        this.contactSales.state = '';
        return;
      }

      this.contactSales.state = 'sent';
    },
  },
  data() {
    return {
      stats: {
        last: {
          in: 0,
          out: 0,
        },
        month: {
          in: 0,
          out: 0,
        },
        total: {
          in: 0,
          out: 0,
        },
      },
      contactSales: {
        show: false,
        state: '',
      },
    };
  },
  mounted() {
    this.getStatistics();
  },
};
</script>
